import type { TypedDocumentNode } from '@apollo/client';
import {
  Kind,
  visit,
  type DefinitionNode,
  type DocumentNode,
  type FragmentDefinitionNode,
  type OperationDefinitionNode,
} from 'graphql';

import FRAGMENTS from './fragments.graphql';
import MUTATIONS from './mutations.graphql';
import type * as M from './mutations.graphql';
import QUERIES from './queries.graphql';
import type * as Q from './queries.graphql';

/**
 * Every Support operation as its own typed document — the one place screens
 * get them from.
 *
 * The Support `.graphql` files hold many operations each, and Apollo refuses a
 * document with more than one; the loader's per-operation named exports are
 * not declared by codegen, and the operations spread fragments that live in
 * `fragments.graphql`. So each operation is cut out of its file here together
 * with exactly the fragments it (transitively) spreads — no more, because
 * GraphQL validation also rejects an unused fragment.
 */
const FRAGMENT_POOL = new Map<string, FragmentDefinitionNode>();
for (const document of [FRAGMENTS, QUERIES, MUTATIONS] as DocumentNode[]) {
  for (const definition of document.definitions) {
    if (definition.kind === Kind.FRAGMENT_DEFINITION && !FRAGMENT_POOL.has(definition.name.value)) {
      FRAGMENT_POOL.set(definition.name.value, definition);
    }
  }
}

function spreadNames(node: DefinitionNode): string[] {
  const names: string[] = [];
  visit(node, {
    FragmentSpread(spread) {
      names.push(spread.name.value);
    },
  });
  return names;
}

/** One named operation from `source`, with the fragments it needs. Throws at load if missing. */
export function pickSupportOperation<TData, TVariables>(
  source: DocumentNode,
  name: string,
): TypedDocumentNode<TData, TVariables> {
  const operation = source.definitions.find(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === Kind.OPERATION_DEFINITION && definition.name?.value === name,
  );
  if (!operation) throw new Error(`Support operation ${name} not found`);
  const included = new Map<string, FragmentDefinitionNode>();
  const pending = spreadNames(operation);
  while (pending.length > 0) {
    const fragmentName = pending.pop() as string;
    if (included.has(fragmentName)) continue;
    const fragment = FRAGMENT_POOL.get(fragmentName);
    if (!fragment) throw new Error(`Support fragment ${fragmentName} not found`);
    included.set(fragmentName, fragment);
    pending.push(...spreadNames(fragment));
  }
  return {
    kind: Kind.DOCUMENT,
    definitions: [operation, ...included.values()],
  } as TypedDocumentNode<TData, TVariables>;
}

export const SUPPORT_TICKETS_QUERY = pickSupportOperation<
  Q.SupportTicketsQuery,
  Q.SupportTicketsQueryVariables
>(QUERIES, 'SupportTickets');
export const SUPPORT_INBOX_COUNTS_QUERY = pickSupportOperation<
  Q.SupportInboxCountsQuery,
  Q.SupportInboxCountsQueryVariables
>(QUERIES, 'SupportInboxCounts');
export const SUPPORT_SEARCH_QUERY = pickSupportOperation<
  Q.SupportSearchQuery,
  Q.SupportSearchQueryVariables
>(QUERIES, 'SupportSearch');
export const SUPPORT_TICKET_QUERY = pickSupportOperation<
  Q.SupportTicketQuery,
  Q.SupportTicketQueryVariables
>(QUERIES, 'SupportTicket');
export const SUPPORT_MESSAGES_QUERY = pickSupportOperation<
  Q.SupportMessagesQuery,
  Q.SupportMessagesQueryVariables
>(QUERIES, 'SupportMessages');
export const SUPPORT_TICKET_EVENTS_QUERY = pickSupportOperation<
  Q.SupportTicketEventsQuery,
  Q.SupportTicketEventsQueryVariables
>(QUERIES, 'SupportTicketEvents');
export const ORGANIZATION_SUPPORT_TICKETS_QUERY = pickSupportOperation<
  Q.OrganizationSupportTicketsQuery,
  Q.OrganizationSupportTicketsQueryVariables
>(QUERIES, 'OrganizationSupportTickets');
export const SUPPORT_CLIENT_CONTEXT_QUERY = pickSupportOperation<
  Q.SupportClientContextQuery,
  Q.SupportClientContextQueryVariables
>(QUERIES, 'SupportClientContext');
export const SUPPORT_CONFIG_QUERY = pickSupportOperation<
  Q.SupportConfigQuery,
  Q.SupportConfigQueryVariables
>(QUERIES, 'SupportConfig');
export const SUPPORT_SETTINGS_QUERY = pickSupportOperation<
  Q.SupportSettingsQuery,
  Q.SupportSettingsQueryVariables
>(QUERIES, 'SupportSettings');
export const SUPPORT_ROLES_QUERY = pickSupportOperation<
  Q.SupportRolesQuery,
  Q.SupportRolesQueryVariables
>(QUERIES, 'SupportRoles');
export const SUPPORT_ATTRIBUTE_SUMMARY_QUERY = pickSupportOperation<
  Q.SupportAttributeSummaryQuery,
  Q.SupportAttributeSummaryQueryVariables
>(QUERIES, 'SupportAttributeSummary');
export const SUPPORT_VIEWER_ACCESS_QUERY = pickSupportOperation<
  Q.SupportViewerAccessQuery,
  Q.SupportViewerAccessQueryVariables
>(QUERIES, 'SupportViewerAccess');
export const SUPPORT_TEAMS_QUERY = pickSupportOperation<
  Q.SupportTeamsQuery,
  Q.SupportTeamsQueryVariables
>(QUERIES, 'SupportTeams');
export const SUPPORT_NOTIFICATIONS_QUERY = pickSupportOperation<
  Q.SupportNotificationsQuery,
  Q.SupportNotificationsQueryVariables
>(QUERIES, 'SupportNotifications');
export const SUPPORT_NOTIFICATION_UNREAD_COUNT_QUERY = pickSupportOperation<
  Q.SupportNotificationUnreadCountQuery,
  Q.SupportNotificationUnreadCountQueryVariables
>(QUERIES, 'SupportNotificationUnreadCount');
export const SUPPORT_NOTIFICATION_PREFERENCES_QUERY = pickSupportOperation<
  Q.SupportNotificationPreferencesQuery,
  Q.SupportNotificationPreferencesQueryVariables
>(QUERIES, 'SupportNotificationPreferences');
export const SUPPORT_METRICS_QUERY = pickSupportOperation<
  Q.SupportMetricsQuery,
  Q.SupportMetricsQueryVariables
>(QUERIES, 'SupportMetrics');
export const SUPPORT_PEOPLE_QUERY = pickSupportOperation<
  Q.SupportPeopleQuery,
  Q.SupportPeopleQueryVariables
>(QUERIES, 'SupportPeople');
export const SUPPORT_PERSON_QUERY = pickSupportOperation<
  Q.SupportPersonQuery,
  Q.SupportPersonQueryVariables
>(QUERIES, 'SupportPerson');
export const REPLY_TO_TICKET_MUTATION = pickSupportOperation<
  M.ReplyToTicketMutation,
  M.ReplyToTicketMutationVariables
>(MUTATIONS, 'ReplyToTicket');
export const UPDATE_SUPPORT_TICKET_MUTATION = pickSupportOperation<
  M.UpdateSupportTicketMutation,
  M.UpdateSupportTicketMutationVariables
>(MUTATIONS, 'UpdateSupportTicket');
export const LOG_SUPPORT_TICKET_MUTATION = pickSupportOperation<
  M.LogSupportTicketMutation,
  M.LogSupportTicketMutationVariables
>(MUTATIONS, 'LogSupportTicket');
export const ASSIGN_SUPPORT_TICKET_MUTATION = pickSupportOperation<
  M.AssignSupportTicketMutation,
  M.AssignSupportTicketMutationVariables
>(MUTATIONS, 'AssignSupportTicket');
export const TAKE_SUPPORT_TICKET_MUTATION = pickSupportOperation<
  M.TakeSupportTicketMutation,
  M.TakeSupportTicketMutationVariables
>(MUTATIONS, 'TakeSupportTicket');
export const SET_SUPPORT_TICKET_AI_ALONGSIDE_MUTATION = pickSupportOperation<
  M.SetSupportTicketAiAlongsideMutation,
  M.SetSupportTicketAiAlongsideMutationVariables
>(MUTATIONS, 'SetSupportTicketAiAlongside');
export const CLOSE_SUPPORT_TICKET_MUTATION = pickSupportOperation<
  M.CloseSupportTicketMutation,
  M.CloseSupportTicketMutationVariables
>(MUTATIONS, 'CloseSupportTicket');
export const REOPEN_SUPPORT_TICKET_MUTATION = pickSupportOperation<
  M.ReopenSupportTicketMutation,
  M.ReopenSupportTicketMutationVariables
>(MUTATIONS, 'ReopenSupportTicket');
export const MARK_SUPPORT_TICKET_READ_MUTATION = pickSupportOperation<
  M.MarkSupportTicketReadMutation,
  M.MarkSupportTicketReadMutationVariables
>(MUTATIONS, 'MarkSupportTicketRead');
export const SET_SUPPORT_TYPING_MUTATION = pickSupportOperation<
  M.SetSupportTypingMutation,
  M.SetSupportTypingMutationVariables
>(MUTATIONS, 'SetSupportTyping');
export const CREATE_SUPPORT_ATTACHMENT_UPLOAD_MUTATION = pickSupportOperation<
  M.CreateSupportAttachmentUploadMutation,
  M.CreateSupportAttachmentUploadMutationVariables
>(MUTATIONS, 'CreateSupportAttachmentUpload');
export const CONFIRM_SUPPORT_ATTACHMENT_UPLOAD_MUTATION = pickSupportOperation<
  M.ConfirmSupportAttachmentUploadMutation,
  M.ConfirmSupportAttachmentUploadMutationVariables
>(MUTATIONS, 'ConfirmSupportAttachmentUpload');
export const MARK_SUPPORT_NOTIFICATIONS_READ_MUTATION = pickSupportOperation<
  M.MarkSupportNotificationsReadMutation,
  M.MarkSupportNotificationsReadMutationVariables
>(MUTATIONS, 'MarkSupportNotificationsRead');
export const UPDATE_SUPPORT_NOTIFICATION_PREFERENCES_MUTATION = pickSupportOperation<
  M.UpdateSupportNotificationPreferencesMutation,
  M.UpdateSupportNotificationPreferencesMutationVariables
>(MUTATIONS, 'UpdateSupportNotificationPreferences');
export const UPDATE_SUPPORT_SETTINGS_MUTATION = pickSupportOperation<
  M.UpdateSupportSettingsMutation,
  M.UpdateSupportSettingsMutationVariables
>(MUTATIONS, 'UpdateSupportSettings');
export const UPSERT_STATUS_WORKFLOW_MUTATION = pickSupportOperation<
  M.UpsertStatusWorkflowMutation,
  M.UpsertStatusWorkflowMutationVariables
>(MUTATIONS, 'UpsertStatusWorkflow');
export const UPSERT_TICKET_TYPE_MUTATION = pickSupportOperation<
  M.UpsertTicketTypeMutation,
  M.UpsertTicketTypeMutationVariables
>(MUTATIONS, 'UpsertTicketType');
export const UPSERT_SUPPORT_ROLE_MUTATION = pickSupportOperation<
  M.UpsertSupportRoleMutation,
  M.UpsertSupportRoleMutationVariables
>(MUTATIONS, 'UpsertSupportRole');
export const DELETE_SUPPORT_ROLE_MUTATION = pickSupportOperation<
  M.DeleteSupportRoleMutation,
  M.DeleteSupportRoleMutationVariables
>(MUTATIONS, 'DeleteSupportRole');
export const ENSURE_SUPPORT_SANDBOX_MUTATION = pickSupportOperation<
  M.EnsureSupportSandboxMutation,
  M.EnsureSupportSandboxMutationVariables
>(MUTATIONS, 'EnsureSupportSandbox');
export const RESET_SUPPORT_SANDBOX_MUTATION = pickSupportOperation<
  M.ResetSupportSandboxMutation,
  M.ResetSupportSandboxMutationVariables
>(MUTATIONS, 'ResetSupportSandbox');
export const CREATE_SUPPORT_SANDBOX_TOKEN_MUTATION = pickSupportOperation<
  M.CreateSupportSandboxTokenMutation,
  M.CreateSupportSandboxTokenMutationVariables
>(MUTATIONS, 'CreateSupportSandboxToken');
export const SUPPORT_SANDBOX_QUERY = pickSupportOperation<
  Q.SupportSandboxQuery,
  Q.SupportSandboxQueryVariables
>(QUERIES, 'SupportSandbox');
export const WEBHOOK_ENDPOINTS_QUERY = pickSupportOperation<
  Q.WebhookEndpointsQuery,
  Q.WebhookEndpointsQueryVariables
>(QUERIES, 'WebhookEndpoints');
export const WEBHOOK_DELIVERIES_QUERY = pickSupportOperation<
  Q.WebhookDeliveriesQuery,
  Q.WebhookDeliveriesQueryVariables
>(QUERIES, 'WebhookDeliveries');
export const CREATE_WEBHOOK_ENDPOINT_MUTATION = pickSupportOperation<
  M.CreateWebhookEndpointMutation,
  M.CreateWebhookEndpointMutationVariables
>(MUTATIONS, 'CreateWebhookEndpoint');
export const UPDATE_WEBHOOK_ENDPOINT_MUTATION = pickSupportOperation<
  M.UpdateWebhookEndpointMutation,
  M.UpdateWebhookEndpointMutationVariables
>(MUTATIONS, 'UpdateWebhookEndpoint');
export const ROTATE_WEBHOOK_ENDPOINT_SECRET_MUTATION = pickSupportOperation<
  M.RotateWebhookEndpointSecretMutation,
  M.RotateWebhookEndpointSecretMutationVariables
>(MUTATIONS, 'RotateWebhookEndpointSecret');
export const DELETE_WEBHOOK_ENDPOINT_MUTATION = pickSupportOperation<
  M.DeleteWebhookEndpointMutation,
  M.DeleteWebhookEndpointMutationVariables
>(MUTATIONS, 'DeleteWebhookEndpoint');
export const SEND_WEBHOOK_ENDPOINT_TEST_MUTATION = pickSupportOperation<
  M.SendWebhookEndpointTestMutation,
  M.SendWebhookEndpointTestMutationVariables
>(MUTATIONS, 'SendWebhookEndpointTest');
export const REPLAY_WEBHOOK_DELIVERY_MUTATION = pickSupportOperation<
  M.ReplayWebhookDeliveryMutation,
  M.ReplayWebhookDeliveryMutationVariables
>(MUTATIONS, 'ReplayWebhookDelivery');
export const SUPPORT_INBOX_FACETS_QUERY = pickSupportOperation<
  Q.SupportInboxFacetsQuery,
  Q.SupportInboxFacetsQueryVariables
>(QUERIES, 'SupportInboxFacets');

export const TICKET_WORK_ITEMS_QUERY = pickSupportOperation<
  Q.TicketWorkItemsQuery,
  Q.TicketWorkItemsQueryVariables
>(QUERIES, 'TicketWorkItems');
export const LINK_WORK_ITEM_MUTATION = pickSupportOperation<
  M.LinkWorkItemMutation,
  M.LinkWorkItemMutationVariables
>(MUTATIONS, 'LinkWorkItem');
export const UNLINK_WORK_ITEM_MUTATION = pickSupportOperation<
  M.UnlinkWorkItemMutation,
  M.UnlinkWorkItemMutationVariables
>(MUTATIONS, 'UnlinkWorkItem');
export const RESYNC_WORK_ITEM_MUTATION = pickSupportOperation<
  M.ResyncWorkItemMutation,
  M.ResyncWorkItemMutationVariables
>(MUTATIONS, 'ResyncWorkItem');
export const EXTERNAL_LISTS_QUERY = pickSupportOperation<
  Q.ExternalListsQuery,
  Q.ExternalListsQueryVariables
>(QUERIES, 'ExternalLists');
export const CREATE_WORK_ITEM_FROM_TICKET_MUTATION = pickSupportOperation<
  M.CreateWorkItemFromTicketMutation,
  M.CreateWorkItemFromTicketMutationVariables
>(MUTATIONS, 'CreateWorkItemFromTicket');
export const CLIENT_STAGES_QUERY = pickSupportOperation<
  Q.ClientStagesQuery,
  Q.ClientStagesQueryVariables
>(QUERIES, 'ClientStages');
export const WORK_ITEM_STATUS_MAPS_QUERY = pickSupportOperation<
  Q.WorkItemStatusMapsQuery,
  Q.WorkItemStatusMapsQueryVariables
>(QUERIES, 'WorkItemStatusMaps');
export const EXTERNAL_LIST_STATUSES_QUERY = pickSupportOperation<
  Q.ExternalListStatusesQuery,
  Q.ExternalListStatusesQueryVariables
>(QUERIES, 'ExternalListStatuses');
export const UPDATE_CLIENT_STAGES_MUTATION = pickSupportOperation<
  M.UpdateClientStagesMutation,
  M.UpdateClientStagesMutationVariables
>(MUTATIONS, 'UpdateClientStages');
export const UPSERT_WORK_ITEM_STATUS_MAP_MUTATION = pickSupportOperation<
  M.UpsertWorkItemStatusMapMutation,
  M.UpsertWorkItemStatusMapMutationVariables
>(MUTATIONS, 'UpsertWorkItemStatusMap');
export const WORK_ITEM_ROUTING_QUERY = pickSupportOperation<
  Q.WorkItemRoutingQuery,
  Q.WorkItemRoutingQueryVariables
>(QUERIES, 'WorkItemRouting');
export const SET_WORK_ITEM_ROUTING_MUTATION = pickSupportOperation<
  M.SetWorkItemRoutingMutation,
  M.SetWorkItemRoutingMutationVariables
>(MUTATIONS, 'SetWorkItemRouting');

export const KNOWLEDGE_HEALTH_QUERY = pickSupportOperation<
  Q.KnowledgeHealthQuery,
  Q.KnowledgeHealthQueryVariables
>(QUERIES, 'KnowledgeHealth');
export const KNOWLEDGE_SOURCES_QUERY = pickSupportOperation<
  Q.KnowledgeSourcesQuery,
  Q.KnowledgeSourcesQueryVariables
>(QUERIES, 'KnowledgeSources');
export const KNOWLEDGE_ARTICLES_QUERY = pickSupportOperation<
  Q.KnowledgeArticlesQuery,
  Q.KnowledgeArticlesQueryVariables
>(QUERIES, 'KnowledgeArticles');
export const KNOWLEDGE_ARTICLE_PATH_GROUPS_QUERY = pickSupportOperation<
  Q.KnowledgeArticlePathGroupsQuery,
  Q.KnowledgeArticlePathGroupsQueryVariables
>(QUERIES, 'KnowledgeArticlePathGroups');
export const KNOWLEDGE_AUDIENCE_RULE_PREVIEW_QUERY = pickSupportOperation<
  Q.KnowledgeAudienceRulePreviewQuery,
  Q.KnowledgeAudienceRulePreviewQueryVariables
>(QUERIES, 'KnowledgeAudienceRulePreview');
export const SET_KNOWLEDGE_AUDIENCE_RULES_MUTATION = pickSupportOperation<
  M.SetKnowledgeAudienceRulesMutation,
  M.SetKnowledgeAudienceRulesMutationVariables
>(MUTATIONS, 'SetKnowledgeAudienceRules');
export const KNOWLEDGE_REVIEW_QUEUE_QUERY = pickSupportOperation<
  Q.KnowledgeReviewQueueQuery,
  Q.KnowledgeReviewQueueQueryVariables
>(QUERIES, 'KnowledgeReviewQueue');
export const AI_RESOLUTION_USAGE_QUERY = pickSupportOperation<
  Q.AiResolutionUsageQuery,
  Q.AiResolutionUsageQueryVariables
>(QUERIES, 'AiResolutionUsage');
export const CREATE_KNOWLEDGE_SOURCE_MUTATION = pickSupportOperation<
  M.CreateKnowledgeSourceMutation,
  M.CreateKnowledgeSourceMutationVariables
>(MUTATIONS, 'CreateKnowledgeSource');
export const CONNECT_CLICKUP_KNOWLEDGE_SOURCE_MUTATION = pickSupportOperation<
  M.ConnectClickUpKnowledgeSourceMutation,
  M.ConnectClickUpKnowledgeSourceMutationVariables
>(MUTATIONS, 'ConnectClickUpKnowledgeSource');
export const CLICKUP_DOC_LOCATIONS_QUERY = pickSupportOperation<
  Q.ClickUpDocLocationsQuery,
  Q.ClickUpDocLocationsQueryVariables
>(QUERIES, 'ClickUpDocLocations');
export const CONNECT_KNOWLEDGE_SOURCE_MUTATION = pickSupportOperation<
  M.ConnectKnowledgeSourceMutation,
  M.ConnectKnowledgeSourceMutationVariables
>(MUTATIONS, 'ConnectKnowledgeSource');
export const CRAWL_KNOWLEDGE_SOURCE_MUTATION = pickSupportOperation<
  M.CrawlKnowledgeSourceMutation,
  M.CrawlKnowledgeSourceMutationVariables
>(MUTATIONS, 'CrawlKnowledgeSource');
export const DELETE_KNOWLEDGE_SOURCE_MUTATION = pickSupportOperation<
  M.DeleteKnowledgeSourceMutation,
  M.DeleteKnowledgeSourceMutationVariables
>(MUTATIONS, 'DeleteKnowledgeSource');
export const SET_KNOWLEDGE_ARTICLE_AUDIENCE_MUTATION = pickSupportOperation<
  M.SetKnowledgeArticleAudienceMutation,
  M.SetKnowledgeArticleAudienceMutationVariables
>(MUTATIONS, 'SetKnowledgeArticleAudience');
export const EXCLUDE_KNOWLEDGE_ARTICLE_MUTATION = pickSupportOperation<
  M.ExcludeKnowledgeArticleMutation,
  M.ExcludeKnowledgeArticleMutationVariables
>(MUTATIONS, 'ExcludeKnowledgeArticle');
export const UPDATE_KNOWLEDGE_SOURCE_MUTATION = pickSupportOperation<
  M.UpdateKnowledgeSourceMutation,
  M.UpdateKnowledgeSourceMutationVariables
>(MUTATIONS, 'UpdateKnowledgeSource');
export const BULK_EXCLUDE_KNOWLEDGE_ARTICLES_MUTATION = pickSupportOperation<
  M.BulkExcludeKnowledgeArticlesMutation,
  M.BulkExcludeKnowledgeArticlesMutationVariables
>(MUTATIONS, 'BulkExcludeKnowledgeArticles');
export const BULK_DELETE_KNOWLEDGE_ARTICLES_MUTATION = pickSupportOperation<
  M.BulkDeleteKnowledgeArticlesMutation,
  M.BulkDeleteKnowledgeArticlesMutationVariables
>(MUTATIONS, 'BulkDeleteKnowledgeArticles');
export const BULK_SET_KNOWLEDGE_ARTICLE_AUDIENCE_MUTATION = pickSupportOperation<
  M.BulkSetKnowledgeArticleAudienceMutation,
  M.BulkSetKnowledgeArticleAudienceMutationVariables
>(MUTATIONS, 'BulkSetKnowledgeArticleAudience');
export const ADD_KNOWLEDGE_DOCUMENT_MUTATION = pickSupportOperation<
  M.AddKnowledgeDocumentMutation,
  M.AddKnowledgeDocumentMutationVariables
>(MUTATIONS, 'AddKnowledgeDocument');
export const SUPPORT_IMPORT_RUNS_QUERY = pickSupportOperation<
  Q.SupportImportRunsQuery,
  Q.SupportImportRunsQueryVariables
>(QUERIES, 'SupportImportRuns');
export const START_INTERCOM_IMPORT_MUTATION = pickSupportOperation<
  M.StartIntercomImportMutation,
  M.StartIntercomImportMutationVariables
>(MUTATIONS, 'StartIntercomImport');
export const CANCEL_SUPPORT_IMPORT_MUTATION = pickSupportOperation<
  M.CancelSupportImportMutation,
  M.CancelSupportImportMutationVariables
>(MUTATIONS, 'CancelSupportImport');
export const SUPPORT_COMPARE_OVERVIEW_QUERY = pickSupportOperation<
  Q.SupportCompareOverviewQuery,
  Q.SupportCompareOverviewQueryVariables
>(QUERIES, 'SupportCompareOverview');
export const SUPPORT_COMPARE_RUNS_QUERY = pickSupportOperation<
  Q.SupportCompareRunsQuery,
  Q.SupportCompareRunsQueryVariables
>(QUERIES, 'SupportCompareRuns');
export const SUPPORT_COMPARE_RUN_DETAIL_QUERY = pickSupportOperation<
  Q.SupportCompareRunDetailQuery,
  Q.SupportCompareRunDetailQueryVariables
>(QUERIES, 'SupportCompareRunDetail');
export const SUPPORT_COMPARE_ESTIMATE_QUERY = pickSupportOperation<
  Q.SupportCompareEstimateQuery,
  Q.SupportCompareEstimateQueryVariables
>(QUERIES, 'SupportCompareEstimate');
export const SUPPORT_COMPARE_RESULTS_QUERY = pickSupportOperation<
  Q.SupportCompareResultsQuery,
  Q.SupportCompareResultsQueryVariables
>(QUERIES, 'SupportCompareResults');
export const START_SUPPORT_COMPARE_MUTATION = pickSupportOperation<
  M.StartSupportCompareMutation,
  M.StartSupportCompareMutationVariables
>(MUTATIONS, 'StartSupportCompare');
export const CANCEL_SUPPORT_COMPARE_MUTATION = pickSupportOperation<
  M.CancelSupportCompareMutation,
  M.CancelSupportCompareMutationVariables
>(MUTATIONS, 'CancelSupportCompare');
export const RESUME_SUPPORT_COMPARE_MUTATION = pickSupportOperation<
  M.ResumeSupportCompareMutation,
  M.ResumeSupportCompareMutationVariables
>(MUTATIONS, 'ResumeSupportCompare');
export const SUPPORT_GUIDELINES_QUERY = pickSupportOperation<
  Q.SupportGuidelinesQuery,
  Q.SupportGuidelinesQueryVariables
>(QUERIES, 'SupportGuidelines');
export const SAVE_SUPPORT_GUIDELINE_MUTATION = pickSupportOperation<
  M.SaveSupportGuidelineMutation,
  M.SaveSupportGuidelineMutationVariables
>(MUTATIONS, 'SaveSupportGuideline');
export const DELETE_SUPPORT_GUIDELINE_MUTATION = pickSupportOperation<
  M.DeleteSupportGuidelineMutation,
  M.DeleteSupportGuidelineMutationVariables
>(MUTATIONS, 'DeleteSupportGuideline');
